// MUST be first: sets globalThis.React before any transitive import of the
// `@experience/shared-ui` barrel (whose components read globalThis.React at
// module-init). ES import hoisting evaluates `./App` before the main.tsx body,
// so the globals cannot live in the body — see bootstrapReactGlobals.ts.
import './bootstrapReactGlobals';
// Capture a `?tour=<id>` deep-link into storage before auth/router can strip it
// (partner-training "Show me in Experience" links). Must run at startup, before App.
import './guide-me/captureTourParam';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useParams, useNavigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import * as ReactQuery from '@tanstack/react-query';
import { AuthProvider, setBaseUrl } from '@empowernow/bff-auth-react';
import { ShieldCheck } from 'lucide-react';
import { EmpThemeProvider } from '@empowernow/ui';
import * as EmpUIAll from '@empowernow/ui';
import App from './App';
// Load EmpowerID Identity Fabric UI CSS first
import '@empowernow/ui/dist/index.css';

// Theme tokens: theme-agnostic base first, then per-theme tokens generated from
// ServiceConfigs themes.yaml (the single source of truth; ThemeManager re-applies
// it at runtime). global.css now carries structural rules only.
import './theme-base.css';
import './theme-generated.css';
import './global.css';
// Shell error surfaces — after global.css so theme tokens beat [data-theme] h1/p resets.
import './plugins/premiumErrorSurface.css';
import { SessionManager } from './core/session/SessionManager';
import { IdlePrompt } from './core/session/IdlePrompt';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { isPublicRoute, fetchPublicRoutes } from './core/auth/publicRoutes';
import { BrandingProvider } from './contexts/BrandingContext';
import { LocalizationProvider } from './contexts/LocalizationContext';

// Construct via namespace to avoid type export mismatch in modern build types
const queryClient = new (ReactQuery as any).QueryClient();
setBaseUrl(import.meta.env.VITE_BFF_BASE_URL || '');

const DEFAULT_THEME_PROPS = { preset: 'neonFlux', algorithm: 'dark' as const };

// React / ReactDOM globals are set in ./bootstrapReactGlobals (imported first)
// so they exist before the @experience/shared-ui barrel initializes.

// Expose React Router hooks for plugins
(window as any).useParams = useParams;
(window as any).useNavigate = useNavigate;
(window as any).useLocation = useLocation;
(globalThis as any).useParams = useParams;
(globalThis as any).useNavigate = useNavigate;
(globalThis as any).useLocation = useLocation;
// Expose EmpowerID Identity Fabric UI component library for plugin bundles
(window as any).EmpUI = EmpUIAll;
// Expose CRUD UI components for plugin bundles
import * as CrudUIAll from './crud-ui';
(window as any).CrudUI = CrudUIAll;
(globalThis as any).CrudUI = CrudUIAll;

// Expose common libraries for plugins (reduces bundle sizes)
import * as jsYaml from 'js-yaml';
(window as any).jsYaml = jsYaml;

// Expose getAuthzenSubject for authorization checks
import { getAuthzenSubject } from './core/auth/subject';
(window as any).getAuthzenSubject = getAuthzenSubject;

// ===== EXPOSE EMPOWERID UI SDK FOR ALL PLUGINS ===== ✅
// Provides: theme-aware components, IT Shop cart, WOW patterns, smart UI
// Components: ShoppingCart, ResourceGrid, AnimatedIconPicker, SmartRecommendations
// ✅ PRODUCTION-READY: Zero TypeScript errors, proper types, commercial SaaS quality!
// See: plugins/shared/empowerid-ui-sdk/README.md for usage
// 🎯 BEST PRACTICE: Dynamic import to ensure React is available first
// - Static imports are hoisted and run before line 1 (before React setup)
// - Dynamic import runs after React is set on globalThis (lines 31-32)
// - Prevents "Cannot read properties of undefined (reading 'useState')" errors
// - Same pattern as Plugin-Auth SDK (line 82)
import('../../plugins/shared/empowerid-ui-sdk/src').then((EmpowerIDUISDK) => {
  (window as any).EmpowerIDUISDK = EmpowerIDUISDK;
  (globalThis as any).EmpowerIDUISDK = EmpowerIDUISDK;
  console.log('✅ EmpowerID UI SDK exposed globally at window.EmpowerIDUISDK');
}).catch((error) => {
  console.error('❌ Failed to load EmpowerID UI SDK:', error);
  console.error('❌ Error details:', error);
});

// Expose Plugin-Auth SDK for plugins (authorization components)
// Use dynamic import to ensure React is available first (imports are hoisted otherwise)
// Note: @empowernow/plugin-auth is aliased in vite.config.ts
// CRITICAL: Export promise so plugins can wait for SDK readiness (prevents race conditions)
export const pluginAuthReady: Promise<void> = import('@empowernow/plugin-auth').then((PluginAuth) => {
  (window as any).PluginAuth = PluginAuth;
  (globalThis as any).PluginAuth = PluginAuth;
  // Also set SharedAuth as alias for backward compatibility with build shims
  (window as any).SharedAuth = PluginAuth;
  (globalThis as any).SharedAuth = PluginAuth;
  console.log('✅ Plugin-Auth SDK exposed to plugins (PluginAuth + SharedAuth)');
}).catch((error) => {
  console.error('❌ Failed to load Plugin-Auth SDK:', error);
  console.error('❌ Error details:', error);
  throw error; // Re-throw so promise rejects
});

// Expose Membership Components globally for ALL plugins
// These are shared UI components for displaying identities, agents, groups, etc.
// NOTE: MUST be synchronous import so components are available BEFORE plugins render!
// (Dynamic imports create race conditions with plugin loading)
import { IdentityCard, IdentityBadge, IdentityAvatar } from './components/membership';
(globalThis as any).MembershipComponents = {
  IdentityCard,
  IdentityBadge,
  IdentityAvatar,
};
console.log('[MembershipComponents] ✓ Exposed globally for plugin use');

// Expose Journey Components globally for ALL plugins
// Shared UI for Universal Journeys cross-plugin navigation (timeline links).
// NOTE: MUST be synchronous import so components are available BEFORE plugins render!
import { JourneyLink } from './components/journey';
(globalThis as any).JourneyComponents = {
  JourneyLink,
};
console.log('[JourneyComponents] ✓ Exposed globally for plugin use');

// Expose MCP UI Components globally for ALL plugins
// These are shared UI components for tool selection, virtual servers, etc.
import {
  UnifiedToolSelector,
  VirtualServerBundleCard,
  VirtualServerTabs,
  ToolCard,
  ToolBrowser,
  ToolSelector,
  useAllMcpServices,
  useAllVirtualServers,
  useAllMcpTools,
  useVirtualServers,
  useMcpTools,
} from './components/mcp';
(globalThis as any).McpUI = {
  // Blue Steel unified experience (multi-server)
  UnifiedToolSelector,
  useAllMcpServices,
  useAllVirtualServers,
  useAllMcpTools,
  // Building blocks
  VirtualServerBundleCard,
  VirtualServerTabs,
  ToolCard,
  ToolBrowser,
  // Legacy single-server
  ToolSelector,
  useVirtualServers,
  useMcpTools,
};
console.log('[McpUI] ✓ Exposed globally (Blue Steel unified experience)');

// ✅ WORKFLOW UI SYSTEM: Universal task rendering with WOW FACTOR FORMS
// - WorkflowUIRenderer: Routes tasks to appropriate handlers  
// - AutoFormHandler: Amazing form experience with confetti, copy buttons, animations
// - Registry: Plugins can register custom handlers
Promise.all([
  import('./core/workflow-ui-registry'),
  import('./components/WorkflowUIRenderer'),
  import('../../plugins/shared/workflow-handlers/AutoFormHandler')
]).then(([{ workflowUIRegistry }, { default: WorkflowUIRenderer }, { AutoFormHandler }]) => {
  // Expose registry globally
  (globalThis as any).workflowUIRegistry = workflowUIRegistry;
  
  // Expose WorkflowUIRenderer globally for plugins
  (globalThis as any).WorkflowUIRenderer = WorkflowUIRenderer;
  
  // ✅ Expose AutoFormHandler globally (WOW factor forms!)
  (globalThis as any).AutoFormHandler = AutoFormHandler;
  
  // ✅ Register AutoFormHandler as default for FORM tasks
  workflowUIRegistry.register('shared', 'auto-form', {
    component: AutoFormHandler,
    description: 'Amazing auto-form with confetti, copy buttons, and WOW factor UX',
    version: '2.0.0',
  });
  
  console.log('✅ Workflow UI system initialized');
  console.log('🎉 AutoFormHandler registered (WOW factor forms enabled!)');
}).catch((error) => {
  console.error('❌ Failed to initialize workflow UI system:', error);
  console.error('FORM tasks will not render properly without AutoFormHandler');
});

// Expose MCP SDK for plugins (MCP tool discovery and invocation)
// Use dynamic import to ensure React is available first
// SDK is aliased in vite.config.ts for reliable module resolution
import('@experience/mcp-sdk').then((McpSDK) => {
  (window as any).McpSDK = McpSDK;
  (globalThis as any).McpSDK = McpSDK;
  console.log('✅ MCP SDK exposed to plugins');
}).catch((error) => {
  console.error('❌ Failed to load MCP SDK:', error);
  // SDK is optional - plugins handle gracefully if not available
});

// ===== EXPOSE SHARED UI COMPONENTS LIBRARY FOR ALL PLUGINS ===== ✅
// Provides: 18 theme-aware, WCAG 2.1 AA compliant UI components
// Components: Button, Card, Input, Modal, Tag, Alert, EmptyState, Skeleton, Toast,
//             DataTable, Pagination, Space, Tooltip, Divider, Popconfirm, Typography, Badge, Spin
// ✅ PRODUCTION-READY: Zero hardcoded colors, full WCAG compliance, SDK-first approach
// See: plugins/shared/ui-components/README.md for usage
// 🎯 BEST PRACTICE: Dynamic import to ensure React is available first
// - Static imports are hoisted and run before line 1 (before React setup)
// - Dynamic import runs after React is set on globalThis (lines 31-32)
// - Prevents "Cannot read properties of undefined (reading 'useState')" errors
// - Same pattern as Plugin-Auth SDK (line 82) and EmpowerID UI SDK (line 71)
import('@experience/shared-ui').then((SharedUI) => {
  (window as any).SharedUI = SharedUI;
  (globalThis as any).SharedUI = SharedUI;
        console.log('✅ Shared UI Components Library exposed globally at window.SharedUI');
        console.log('   Available components: Button, Card, Input, Modal, Tag, Alert, EmptyState, Skeleton, Toast, DataTable, Pagination, Space, Tooltip, Divider, Popconfirm, Typography, Badge, Spin, AutoComplete, TextArea, List, Progress, Descriptions, Form, Tabs, Collapse, Segmented, Steps, Dropdown, Upload');
}).catch((error) => {
  console.error('❌ Failed to load Shared UI Components Library:', error);
  console.error('❌ Error details:', error);
});

// Import AuthZEN Initializer for permission preloading
import { AuthZENInitializer } from './core/auth/AuthZENInitializer';

// Import Quick Access Context for recent plugins sidebar
import { QuickAccessProvider } from './contexts/QuickAccessContext';

// Mermaid will be lazy-loaded by plugins that need it
// We expose it here so plugins can access from globalThis if needed
import('mermaid').then((mermaidModule) => {
  (window as any).mermaid = mermaidModule.default;
});

// Quick runtime check to warn if core CSS variables are missing (theme not loaded)
if (typeof window !== 'undefined') {
  const root = document.documentElement;
  const surface = getComputedStyle(root).getPropertyValue('--color-surface');
  if (!surface || surface.trim().length === 0) {
    console.warn('[NeonFlux] Theme tokens not detected. Verify @empowernow/ui/dist/index.css loaded and CSP allows font/css.');
  }
}

// Wrapper component to use our theme with EmpThemeProvider
function ThemedApp() {
  const { theme } = useTheme();
  
  // Map our themes to EmpThemeProvider settings
  // Light themes use 'default' algorithm; everything else uses 'dark'
  const lightThemes = ['white', 'nomadid'];
  const empThemeAlgorithm = lightThemes.includes(theme) ? 'default' : 'dark';

  return (
    <EmpThemeProvider preset="neonFlux" algorithm={empThemeAlgorithm}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <BrandingProvider>
            <RequireAuth>
              <QuickAccessProvider>
              <AuthZENInitializer
                customPreload={[
                  // ═══════════════════════════════════════════════════════════
                  // App.tsx Guards (Checked on every app load)
                  // ═══════════════════════════════════════════════════════════
                  { resource: 'pages', actions: ['view_all'] },
                  { resource: 'workflows', actions: ['view_all'] },
                  { resource: 'tasks', actions: ['view_all'] },
                  
                  // ═══════════════════════════════════════════════════════════
                  // LayoutShell Navigation (Checked for nav items)
                  // ═══════════════════════════════════════════════════════════
                  { resource: 'experience.route', actions: ['view'] },
                  
                  // ═══════════════════════════════════════════════════════════
                  // Dashboard Widgets (8 widgets check these)
                  // ═══════════════════════════════════════════════════════════
                  { resource: 'tasks', actions: ['view_summary'] },
                  // Note: pages and workflows already in list above
                  
                  // ═══════════════════════════════════════════════════════════
                  // Common Features (Used by most users)
                  // ═══════════════════════════════════════════════════════════
                  { resource: 'dashboard', actions: ['view'] },
                  { resource: 'home', actions: ['view'] },
                  { resource: 'catalog', actions: ['view'] },
                  { resource: 'profile', actions: ['view', 'edit'] },
                  { resource: 'theme', actions: ['view', 'edit'] },
                  { resource: 'experience:theme-picker', actions: ['change-theme'] },
                  { resource: 'settings', actions: ['view', 'edit'] },
                  
                  // ═══════════════════════════════════════════════════════════
                  // Common Plugin Resources (Instant plugin loading)
                  // ═══════════════════════════════════════════════════════════
                  { resource: 'plugin.widget', actions: ['view'] },
                  { resource: 'user', actions: ['view', 'edit'] },
                  { resource: 'admin-panel', actions: ['view'] }
                ]}
                showLoading={false}  // Don't duplicate loading screen (already have app-loading div)
                debug={import.meta.env.DEV}
                onReady={(result) => {
                  console.log(
                    `[Experience] ✅ Preloaded ${result.preloadedCount} permissions in ${result.duration.toFixed(0)}ms. ` +
                    `Dashboard will render instantly!`
                  );
                }}
                onError={(error) => {
                  console.error('[Experience] Preload failed (app will continue with on-demand checks):', error);
                }}
              >
                <App />
                <SessionManager />
                <IdlePrompt />
              </AuthZENInitializer>
              </QuickAccessProvider>
            </RequireAuth>
          </BrandingProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </EmpThemeProvider>
  );
}

// =============================================================================
// FAIL-CLOSED SECURITY: Wait for Plugin-Auth SDK before rendering
// =============================================================================
// As an identity product, we MUST ensure the authorization SDK is loaded BEFORE
// any plugins render. This prevents:
// 1. Plugins rendering with stub/fallback authorization (security hole)
// 2. Race conditions where plugins capture empty globalThis.PluginAuth
// 3. Inconsistent authorization state during page load
//
// Commercial SaaS production-quality approach: Block render until SDK ready.
// =============================================================================
// Warm public-route cache early so RequireAuth and plugin loader agree on public paths.
fetchPublicRoutes();

pluginAuthReady
  .then(() => {
    console.log('[Security] ✅ Plugin-Auth SDK ready - rendering with fail-closed security');
    
    createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <AuthProvider baseUrl={import.meta.env.VITE_BFF_BASE_URL || ''} pollIntervalMs={300000}>
          <LocalizationProvider>
            <ThemeProvider>
              <ThemedApp />
            </ThemeProvider>
          </LocalizationProvider>
        </AuthProvider>
      </React.StrictMode>
    );

    // Register Service Worker for PWA (push notifications, offline app shell)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          console.log('[SW] Registered, scope:', reg.scope);
          reg.addEventListener('updatefound', () => {
            const newSW = reg.installing;
            if (!newSW) return;
            newSW.addEventListener('statechange', () => {
              if (newSW.state === 'activated' && navigator.serviceWorker.controller) {
                console.log('[SW] New version activated');
              }
            });
          });
        })
        .catch((err) => console.warn('[SW] Registration failed:', err));
    }

    // Hide loading screen with smooth fade-out (prevents white flash)
    setTimeout(() => {
      const loadingScreen = document.getElementById('app-loading');
      if (loadingScreen) {
        loadingScreen.classList.add('hidden');
        // Remove from DOM after fade animation completes
        setTimeout(() => loadingScreen.remove(), 300);
      }
    }, 100); // Small delay to ensure React has painted
  })
  .catch((error) => {
    // FAIL-CLOSED: SDK failed to load - show security error, do NOT render app
    console.error('[Security] ❌ CRITICAL: Plugin-Auth SDK failed to load');
    console.error('[Security] ❌ Error:', error);
    
    const loadingScreen = document.getElementById('app-loading');
    if (loadingScreen) {
      loadingScreen.textContent = '';

      const wrapper = document.createElement('div');
      Object.assign(wrapper.style, {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh',
        background: 'var(--main-bg, #1a1a2e)',
        color: 'var(--error-color, #ff6b6b)', fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '24px', textAlign: 'center',
      });

      // Governed lock glyph as a dependency-free inline Lucide SVG (outline,
      // 1.5px stroke) — this fatal path runs without React, so we cannot use the
      // <ShieldCheck> component; an emoji would violate the icon convention.
      const icon = document.createElement('div');
      Object.assign(icon.style, {
        marginBottom: '16px', display: 'flex', color: 'var(--error-color, #ff6b6b)',
      });
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" '
        + 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" '
        + 'stroke-linecap="round" stroke-linejoin="round">'
        + '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>'
        + '<path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

      const heading = document.createElement('h1');
      Object.assign(heading.style, { fontSize: '24px', marginBottom: '8px', color: 'var(--main-text, #fff)' });
      heading.textContent = 'Security Initialization Failed';

      const desc = document.createElement('p');
      Object.assign(desc.style, { color: 'var(--secondary-text, #aaa)', maxWidth: '400px', marginBottom: '24px', lineHeight: '1.5' });
      desc.textContent = 'The authorization system failed to initialize. For security, the application cannot continue without proper authorization controls.';

      const btn = document.createElement('button');
      Object.assign(btn.style, {
        padding: '12px 24px',
        background: 'var(--button-primary-bg, var(--quantum-violet, #4a4a6a))',
        color: 'var(--color-on-accent, #fff)',
        border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
      });
      btn.textContent = 'Retry';
      btn.addEventListener('click', () => window.location.reload());

      const footer = document.createElement('p');
      Object.assign(footer.style, { color: 'var(--secondary-text, #666)', fontSize: '12px', marginTop: '24px' });
      footer.textContent = 'If this problem persists, contact your administrator.';

      wrapper.append(icon, heading, desc, btn, footer);
      loadingScreen.appendChild(wrapper);
    }
  });

// ============================================================================
// MAGNUM: Session Check Loading Component
// Shows while checking authentication state
// ============================================================================

function SessionCheckLoader() {
  const [visible, setVisible] = React.useState(false);
  const [dotCount, setDotCount] = React.useState(0);
  
  React.useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);
  
  const dots = '.'.repeat(dotCount);
  
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Checking session"
      data-testid="experience-session-loader"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        background: 'var(--main-bg, #f5f7fb)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}
    >
      {/* Security icon with glow (decorative — the role=status container announces state) */}
      <div
        aria-hidden="true"
        style={{
          position: 'relative',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 100ms',
        }}
      >
        {/* Outer glow pulse (violet accent, decorative) */}
        <div
          style={{
            position: 'absolute',
            inset: '-20px',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--quantum-violet, #6C4CFF) 20%, transparent), transparent 70%)',
            animation: 'sessionGlowPulse 2s ease-in-out infinite',
          }}
        />
        {/* Orbiting ring */}
        <div
          style={{
            position: 'absolute',
            inset: '-12px',
            borderRadius: '50%',
            border:
              '2px dashed color-mix(in srgb, var(--quantum-violet, #6C4CFF) 30%, transparent)',
            animation: 'sessionOrbit 6s linear infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--quantum-violet, #6C4CFF)',
              boxShadow:
                '0 0 12px color-mix(in srgb, var(--quantum-violet, #6C4CFF) 60%, transparent)',
            }}
          />
        </div>
        {/* Governed Lucide icon (outline, 1.5px stroke) — violet acts, no amber emoji */}
        <ShieldCheck
          size={48}
          strokeWidth={1.5}
          color="var(--main-accent, var(--quantum-violet, #6C4CFF))"
          style={{ animation: 'sessionLockPulse 2s ease-in-out infinite' }}
        />
      </div>
      
      {/* Text */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.5s ease 200ms',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--main-text, #0f172a)',
            marginBottom: '8px',
            minWidth: '200px',
          }}
        >
          Checking your session{dots}
        </div>
        <div
          style={{
            fontSize: '14px',
            color: 'var(--secondary-text, #5b6472)',
          }}
        >
          Verifying authentication
        </div>
      </div>
      
      {/* Animated dots (decorative) */}
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          gap: '8px',
          marginTop: '24px',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.5s ease 300ms',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: 'var(--quantum-violet, #6C4CFF)',
              opacity: 0.3,
              animation: `sessionDotPulse 1.2s ease-in-out infinite ${i * 200}ms`,
            }}
          />
        ))}
      </div>
      
      {/* Inject animations */}
      <style>{`
        @keyframes sessionGlowPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes sessionOrbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes sessionLockPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes sessionDotPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="experience-session-loader"],
          [data-testid="experience-session-loader"] * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const [ready, setReady] = React.useState<boolean>(false);
  const [allowed, setAllowed] = React.useState<boolean>(false);
  const IS_TEST = typeof (globalThis as any).__VITEST__ !== 'undefined'
    || (typeof import.meta !== 'undefined' && (import.meta as any).vitest)
    || (globalThis as any).IS_VITEST === true;

  const isAuthRoute = path === '/login' || path.startsWith('/auth') || path.startsWith('/action/');
  const isSyncPublic = isPublicRoute(path);

  React.useEffect(() => {
    if (isAuthRoute || isSyncPublic) {
      console.log('[RequireAuth] Sync bypass:', { isAuthRoute, isSyncPublic, path });
      setAllowed(true);
      setReady(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        console.log('[RequireAuth] Fetching public routes + session for:', path);
        const [publicPrefixes, sessionModule] = await Promise.all([
          fetchPublicRoutes(),
          import('./core/session/SessionSingleFlight'),
        ]);

        if (cancelled) return;

        console.log('[RequireAuth] Public prefixes:', publicPrefixes);
        const isPublic = publicPrefixes.some(prefix => path.startsWith(prefix));

        if (isPublic) {
          console.log('[RequireAuth] Path is public, allowing');
          setAllowed(true);
          setReady(true);
          return;
        }

        sessionModule.invalidateSessionCache();
        const session = await sessionModule.fetchSessionOnce();
        if (cancelled) return;

        const authed = !!(session as any)?.authenticated;
        console.log('[RequireAuth] Session check:', { authed });

        if (authed) {
          setAllowed(true);
        } else if (!IS_TEST) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/auth/login?return_to=${returnUrl}`);
        }
        setReady(true);
      } catch (err) {
        console.error('[RequireAuth] Error during auth check:', err);
        if (!cancelled) {
          setReady(true);
          if (!IS_TEST) {
            const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.replace(`/auth/login?return_to=${returnUrl}`);
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (isAuthRoute || isSyncPublic) {
    return <>{children}</>;
  }

  if (!ready || !allowed) {
    return <SessionCheckLoader />;
  }
  return <>{children}</>;
}


